import useFormValidation from "hooks/useFormValidation";
import { Label } from "layout/typography/Label";
import { Button } from "primereact/button";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { Tooltip } from "primereact/tooltip";
import { useRef, useState } from "react";
import { Controller, useFormContext, useFieldArray } from "react-hook-form";
import styled from "styled-components";

import { formValidationConstants } from "../helpers/formValidations";

const LocationChip = styled.div<{ $editing?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  background: ${({ $editing }) => ($editing ? "" : "#eef2ff")};
  border: 1px solid
    ${({ $editing }) => ($editing ? "var(--primary-color)" : "transparent")};
  min-height: 2.25rem;
  min-width: 8rem;
  input {
    border: none !important;
    box-shadow: none !important;
    background: transparent;
    outline: none;
    padding: 0;
  }
  &[data-editing="false"] {
    color: var(--primary-color);
    font-weight: var(--font-weight-semibold);
  }
  &[data-editing="false"] .chip-actions {
    opacity: 0;
    transition: opacity 0.15s ease;
  }
  &[data-editing="false"]:hover .chip-actions {
    opacity: 1;
  }
`;

export function HospitalLocations() {
  const { control, trigger, setValue, watch } = useFormContext<any>();
  const [expanded, setExpanded] = useState(false);
  const accountLocations = watch("accountLocations") ?? [];
  const { handleOnBlurField } = useFormValidation();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "accountLocations",
  });
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const previousValueRef = useRef<string>("");

  const handleAddLocation = () => {
    append({ name: "" });
    setEditingIndex(fields.length);
  };

  const handleEdit = (index: number, currentName: string) => {
    previousValueRef.current = currentName ?? "";
    setEditingIndex(index);
  };

  const handleSave = (index: number) => {
    setEditingIndex(null);
    trigger(`accountLocations.${index}.name`);
  };

  const handleCancelEdit = (index: number) => {
    const wasNewAndEmpty = previousValueRef.current === "";
    setEditingIndex(null);
    if (wasNewAndEmpty) {
      remove(index);
    } else {
      setValue(`accountLocations.${index}.name`, previousValueRef.current);
    }
  };

  return (
    <div className="block bg-white border-round-xl border-1 border-gray-300 p-4">
      <Tooltip target=".delete-location-disabled-tooltip" position="top" />
      <button
        type="button"
        className="flex align-items-center justify-content-between w-full border-none bg-transparent p-0 cursor-pointer text-left"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <span className="text-black font-bold text-lg m-0">
          Hospital Locations
        </span>
        <i
          className={`pi pi-chevron-down text-gray-600 transition-duration-200 transition-all ${
            expanded ? "rotate-180" : ""
          }`}
          style={{ fontSize: "0.75rem" }}
          aria-hidden
        />
      </button>
      {expanded && (
        <div className="flex flex-column gap-4">
          <div className="flex flex-column gap-3 w-full mt-4 mb-4">
            <span className="font-medium text-sm text-gray-600 mb-4">
              ENABLE LOCATIONS FOR
            </span>
            <div className="grid formgrid">
              <div className="col-12 md:col-4 flex gap-3 align-items-center mb-4">
                <Controller
                  name="showLocationsPatientResponsibility"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <InputSwitch
                      inputId="showLocationsPatientResponsibility"
                      checked={value || false}
                      onChange={(e) => onChange(e.value)}
                    />
                  )}
                />
                <span className="font-medium text-sm">
                  Patient Responsibility
                </span>
              </div>
              <div className="col-12 md:col-4 flex gap-3 align-items-center mb-4">
                <Controller
                  name="showLocationsEd"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <InputSwitch
                      inputId="showLocationsEd"
                      checked={value || false}
                      onChange={(e) => onChange(e.value)}
                    />
                  )}
                />
                <span className="font-medium text-sm">ER</span>
              </div>
              <div className="col-12 md:col-4 flex gap-3 align-items-center mb-4">
                <Controller
                  name="showLocationsBariatrics"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <InputSwitch
                      inputId="showLocationsBariatrics"
                      checked={value || false}
                      onChange={(e) => onChange(e.value)}
                    />
                  )}
                />
                <span className="font-medium text-sm">Bariatrics</span>
              </div>
              <div className="col-12 md:col-4 flex gap-3 align-items-center mb-4">
                <Controller
                  name="showLocationsGfe"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <InputSwitch
                      inputId="showLocationsGfe"
                      checked={value || false}
                      onChange={(e) => onChange(e.value)}
                    />
                  )}
                />
                <span className="font-medium text-sm">Clear Estimate Pro</span>
              </div>
              <div className="col-12 md:col-4 flex gap-3 align-items-center mb-4">
                <Controller
                  name="showLocationsBundled"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <InputSwitch
                      inputId="showLocationsBundled"
                      checked={value || false}
                      onChange={(e) => onChange(e.value)}
                    />
                  )}
                />
                <span className="font-medium text-sm">Clear Packages</span>
              </div>
              <div className="col-12 md:col-4 flex gap-3 align-items-center mb-4">
                <Controller
                  name="showLocationsDirectPay"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <InputSwitch
                      inputId="showLocationsDirectPay"
                      checked={value || false}
                      onChange={(e) => onChange(e.value)}
                    />
                  )}
                />
                <span className="font-medium text-sm">Direct Pay</span>
              </div>
            </div>
          </div>

          <div className="field col-12 flex align-items-center justify-content-between flex-wrap gap-2">
            <span className="text-color-secondary">
              {fields.length} location{fields.length !== 1 ? "s" : ""}
            </span>
            <Button
              type="button"
              label="Add Location"
              className="p-button-outlined"
              icon="pi pi-plus"
              onClick={handleAddLocation}
              disabled={editingIndex !== null}
            />
          </div>

          {fields.length > 0 && (
            <div className="field col-12 flex flex-wrap gap-2 align-items-start">
              {fields.map((field, index) => (
                <Controller
                  key={field.id}
                  name={`accountLocations.${index}.name`}
                  control={control}
                  rules={{
                    required:
                      formValidationConstants.HOSPITAL_LOCATIONS.LOCATION_NAME,
                  }}
                  render={({
                    field: { onChange, onBlur, value },
                    fieldState,
                  }) => {
                    const isEditing = editingIndex === index;
                    return (
                      <LocationChip
                        $editing={isEditing}
                        data-editing={isEditing}
                      >
                        {isEditing ? (
                          <>
                            <div className="chip-input-wrapper flex-1 flex flex-column gap-1">
                              <InputText
                                value={value || ""}
                                placeholder="Location name"
                                onChange={(e) => {
                                  onChange(e);
                                  if (!e.target.value?.trim()) {
                                    trigger(`accountLocations.${index}.name`);
                                  }
                                }}
                                onBlur={() =>
                                  handleOnBlurField({
                                    onBlur,
                                    field: `accountLocations.${index}.name`,
                                    trigger,
                                  })
                                }
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") handleSave(index);
                                  if (e.key === "Escape")
                                    handleCancelEdit(index);
                                }}
                                aria-label={`Location ${index + 1} name`}
                                aria-invalid={Boolean(fieldState.error)}
                                autoFocus
                              />
                              {fieldState.error?.message && (
                                <small className="p-error">
                                  {fieldState.error.message}
                                </small>
                              )}
                            </div>
                            <Button
                              type="button"
                              icon="pi pi-check"
                              className="p-0 p-button-text px-0 w-fit h-fit p-button-sm p-button-success"
                              onClick={() => handleSave(index)}
                              aria-label="Save"
                            />
                            <Button
                              type="button"
                              icon="pi pi-times"
                              className="p-button-text p-0 w-fit h-fit p-button-danger ml-2 p-button-sm"
                              onClick={() => handleCancelEdit(index)}
                              aria-label="Cancel"
                            />
                          </>
                        ) : (
                          <>
                            <span
                              className="flex-1"
                              style={{ minWidth: "100px" }}
                            >
                              {value?.trim() || <span>Location name</span>}
                            </span>
                            <div className="chip-actions flex align-items-center gap-0">
                              <Button
                                type="button"
                                icon="pi pi-pencil"
                                className="p-0 p-button-text px-0 w-fit h-fit p-button-sm"
                                onClick={() => handleEdit(index, value ?? "")}
                                aria-label="Edit"
                              />
                              <span
                                className={
                                  accountLocations[index]?.alreadyUsedInOrders
                                    ? "delete-location-disabled-tooltip"
                                    : ""
                                }
                                data-pr-tooltip={
                                  accountLocations[index]?.alreadyUsedInOrders
                                    ? "This location can not be deleted as it has already been used"
                                    : undefined
                                }
                              >
                                <Button
                                  type="button"
                                  icon="pi pi-times"
                                  className="p-button-text p-0 w-fit h-fit p-button-danger ml-2 p-button-sm"
                                  onClick={() => {
                                    if (
                                      !accountLocations[index]
                                        ?.alreadyUsedInOrders
                                    ) {
                                      if (editingIndex === index)
                                        setEditingIndex(null);
                                      remove(index);
                                    }
                                  }}
                                  disabled={
                                    accountLocations[index]?.alreadyUsedInOrders
                                  }
                                  aria-label="Remove"
                                />
                              </span>
                            </div>
                          </>
                        )}
                      </LocationChip>
                    );
                  }}
                />
              ))}
            </div>
          )}
          {!fields.length && (
            <p className="text-color-secondary mb-0 mt-2">
              No locations configured. Click &quot;Add Location&quot; to add
              locations.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
